import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const REASON_MAX = 1000;

const CSAT_LABELS = { 1: 'Very dissatisfied', 2: 'Dissatisfied', 3: 'Neutral', 4: 'Satisfied', 5: 'Very satisfied' };
const CES_LABELS = { 1: 'Very difficult', 5: 'Very easy' };

const MATRIX_ROWS = [
  ['rating_cleanliness', 'Cleanliness'],
  ['rating_wifi', 'Wi-Fi'],
  ['rating_ac_lighting', 'AC & lighting'],
  ['rating_meeting_rooms', 'Meeting rooms'],
  ['rating_front_desk', 'Front desk team'],
  ['rating_maintenance', 'Maintenance'],
  ['rating_value', 'Value for money'],
];

const IMPROVEMENT_OPTIONS = [
  ['wifi', 'Wi-Fi'],
  ['meeting_rooms', 'Meeting rooms'],
  ['cleanliness', 'Cleanliness'],
  ['f_and_b', 'F&B'],
  ['events', 'Events'],
  ['faster_resolution', 'Faster resolution'],
  ['other', 'Other'],
];

const RENEWAL_OPTIONS = [
  ['definitely', 'Definitely'],
  ['probably', 'Probably'],
  ['unsure', 'Unsure'],
  ['probably_not', 'Probably not'],
  ['definitely_not', 'Definitely not'],
];

const Optional = () => <span className="text-muted-foreground font-normal">(optional)</span>;

// Row of numbered pill buttons; `extra` adds a trailing non-numeric choice (N/A etc.).
const ScaleButtons = ({ values, value, onChange, extra, testid, size = 'md' }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {values.map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(value === n ? null : n)}
        className={`${size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm'} rounded-md border font-medium transition-colors ${
          value === n ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'
        }`}
        aria-pressed={value === n}
        data-testid={`${testid}-${n}`}
      >
        {n}
      </button>
    ))}
    {extra && (
      <button
        type="button"
        onClick={() => onChange(value === extra.value ? null : extra.value)}
        className={`${size === 'sm' ? 'h-8 text-xs' : 'h-10 text-sm'} px-3 rounded-md border font-medium transition-colors ${
          value === extra.value ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'
        }`}
        aria-pressed={value === extra.value}
        data-testid={`${testid}-${extra.value}`}
      >
        {extra.label}
      </button>
    )}
  </div>
);

const MemberFeedbackPage = () => {
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [centres, setCentres] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [centre, setCentre] = useState(searchParams.get('centre') || '');
  const [csat, setCsat] = useState(null);
  const [nps, setNps] = useState(null);
  const [reason, setReason] = useState('');
  const [matrix, setMatrix] = useState({});
  const [ces, setCes] = useState(null);
  const [areas, setAreas] = useState([]);
  const [areasOther, setAreasOther] = useState('');
  const [renewal, setRenewal] = useState('');
  const [contactOk, setContactOk] = useState(false);
  const [testimonialOk, setTestimonialOk] = useState(false);
  const [website, setWebsite] = useState(''); // honeypot

  // Not linked from the platform — keep it out of search engines too.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/member-feedback/form`);
        if (!cancelled) setCentres(res.data.centres || []);
      } catch (err) {
        if (!cancelled) setError('Could not load the form. Please try again in a moment.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const toggleArea = (key, checked) => {
    setAreas((prev) => (checked ? [...prev, key] : prev.filter((a) => a !== key)));
  };

  const handleSubmit = async () => {
    if (!EMAIL_RE.test(email.trim())) return toast.error('Please enter a valid work email');
    if (!centre) return toast.error('Please select your Clayworks centre');
    if (!csat) return toast.error('Please rate your overall satisfaction');
    if (nps === null) return toast.error('Please tell us how likely you are to recommend Clayworks');
    if (!reason.trim()) return toast.error("Please tell us the main reason for your score");

    setSubmitting(true);
    try {
      await axios.post(`${API}/member-feedback/submit`, {
        email: email.trim(),
        feedback_centre: centre,
        csat_score: csat,
        nps_score: nps,
        feedback_reason: reason.trim(),
        ...Object.fromEntries(MATRIX_ROWS.map(([k]) => [k, matrix[k] ?? null])),
        ces_score: ces,
        improvement_areas: areas,
        improvement_other: areas.includes('other') ? areasOther.trim() || null : null,
        renewal_intent: renewal || null,
        feedback_contact_ok: contactOk,
        testimonial_ok: testimonialOk,
        website,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted dark:bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted dark:bg-background p-4" data-testid="member-feedback-error">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3">
            <AlertTriangle className="w-12 h-12 mx-auto text-amber-500" />
            <h2 className="text-xl font-heading">Cannot open feedback form</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted dark:bg-background p-4" data-testid="member-feedback-success">
        <Card className="max-w-md w-full">
          <CardContent className="p-10 text-center space-y-4">
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-500" />
            <h2 className="text-2xl font-heading">Thank you!</h2>
            <p className="text-muted-foreground">Your feedback has been recorded. We read every response.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted dark:bg-background py-10 px-4" data-testid="member-feedback-page">
      <div className="max-w-2xl lg:max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <img src="/logo.webp" alt="Clayworks" className="h-10 w-auto mx-auto" />
          <p className="text-xs text-muted-foreground tracking-widest mt-2">MEMBER FEEDBACK</p>
        </div>

        <Card className="bg-card shadow-md border-border/60">
          <CardContent className="p-6 lg:p-10 space-y-8">
            <div>
              <h2 className="text-2xl lg:text-3xl font-heading mb-2">How’s your workspace working for you?</h2>
              <p className="text-sm lg:text-base text-muted-foreground">
                Your workspace should work as hard as you do. We’re asking every client across our centres for honest
                feedback, so we can fix what’s not working and do more of what is. It takes about 2 minutes, and every
                response is read by our team.
              </p>
              <p className="text-xs text-muted-foreground mt-3">Questions marked * are required.</p>
            </div>

            {/* Honeypot field — hidden from real members, bots tend to fill every field */}
            <input
              type="text"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              className="absolute left-[-9999px] w-px h-px opacity-0"
              aria-hidden="true"
            />

            {/* Q1 */}
            <div className="space-y-2">
              <Label htmlFor="mf-email">1. Work email *</Label>
              <Input
                id="mf-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="mf-email-input"
              />
            </div>

            {/* Q2 */}
            <div className="space-y-2">
              <Label>2. Which Clayworks centre are you based at? *</Label>
              <Select value={centre} onValueChange={setCentre}>
                <SelectTrigger data-testid="mf-centre-select"><SelectValue placeholder="Select centre" /></SelectTrigger>
                <SelectContent>
                  {centres.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}{c.city ? ` (${c.city})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Q3 */}
            <div className="space-y-2">
              <Label>3. Overall, how satisfied are you with your Clayworks workspace? *</Label>
              <ScaleButtons values={[1, 2, 3, 4, 5]} value={csat} onChange={setCsat} testid="mf-csat" />
              <p className="text-xs text-muted-foreground">{csat ? `${csat} — ${CSAT_LABELS[csat]}` : '1 = Very dissatisfied, 5 = Very satisfied'}</p>
            </div>

            {/* Q4 */}
            <div className="space-y-2">
              <Label>4. How likely are you to recommend Clayworks to a friend or business contact? *</Label>
              {/* inline-flex so the end labels span exactly the width of the button row */}
              <div className="inline-flex flex-col gap-2">
                <ScaleButtons values={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]} value={nps} onChange={setNps} testid="mf-nps" size="sm" />
                <div className="flex justify-between gap-4 text-xs text-muted-foreground">
                  <span>0 = Not at all likely</span><span>10 = Extremely likely</span>
                </div>
              </div>
            </div>

            {/* Q5 */}
            <div className="space-y-2">
              <Label htmlFor="mf-reason">5. What's the main reason for your score? *</Label>
              <Textarea
                id="mf-reason"
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
                maxLength={REASON_MAX}
                data-testid="mf-reason-input"
              />
              <p className="text-xs text-muted-foreground text-right">{reason.length} / {REASON_MAX}</p>
            </div>

            {/* Q6 */}
            <div className="space-y-3">
              <Label>6. Please rate the following <Optional /></Label>
              <p className="text-xs text-muted-foreground">1 = Poor, 5 = Excellent</p>
              <div className="divide-y rounded-md border">
                {MATRIX_ROWS.map(([key, label]) => (
                  <div key={key} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3">
                    <span className="text-sm">{label}</span>
                    <ScaleButtons
                      values={[1, 2, 3, 4, 5]}
                      value={matrix[key] ?? null}
                      onChange={(v) => setMatrix((m) => ({ ...m, [key]: v }))}
                      extra={{ value: 'na', label: 'N/A' }}
                      testid={`mf-${key}`}
                      size="sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Q7 */}
            <div className="space-y-2">
              <Label>7. When you raised an issue, how easy was it to get it resolved? <Optional /></Label>
              <ScaleButtons
                values={[1, 2, 3, 4, 5]}
                value={ces}
                onChange={setCes}
                extra={{ value: 'not_raised', label: "Haven't raised an issue" }}
                testid="mf-ces"
              />
              <p className="text-xs text-muted-foreground">1 = {CES_LABELS[1]}, 5 = {CES_LABELS[5]}</p>
            </div>

            {/* Q8 */}
            <div className="space-y-3">
              <Label>8. What would most improve your experience? <Optional /></Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {IMPROVEMENT_OPTIONS.map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={areas.includes(key)}
                      onCheckedChange={(c) => toggleArea(key, c === true)}
                      data-testid={`mf-improve-${key}`}
                    />
                    {label}
                  </label>
                ))}
              </div>
              {areas.includes('other') && (
                <Input
                  value={areasOther}
                  onChange={(e) => setAreasOther(e.target.value)}
                  placeholder="Please specify"
                  maxLength={500}
                  data-testid="mf-improve-other-input"
                />
              )}
            </div>

            {/* Q9 */}
            <div className="space-y-3">
              <Label>9. How likely are you to renew when your term ends? <Optional /></Label>
              <RadioGroup value={renewal} onValueChange={setRenewal} className="space-y-1" data-testid="mf-renewal">
                {RENEWAL_OPTIONS.map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <RadioGroupItem value={key} data-testid={`mf-renewal-${key}`} />
                    {label}
                  </label>
                ))}
              </RadioGroup>
            </div>

            {/* Q10 */}
            <div className="space-y-3">
              <Label>10. Permissions <Optional /></Label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox checked={contactOk} onCheckedChange={(c) => setContactOk(c === true)} className="mt-0.5" data-testid="mf-contact-ok" />
                You may contact me about this feedback
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox checked={testimonialOk} onCheckedChange={(c) => setTestimonialOk(c === true)} className="mt-0.5" data-testid="mf-testimonial-ok" />
                You may use my feedback as a testimonial
              </label>
            </div>

            <Button onClick={handleSubmit} disabled={submitting} className="w-full" size="lg" data-testid="mf-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Feedback
            </Button>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Clayworks Spaces Technologies Pvt Ltd
        </p>
      </div>
    </div>
  );
};

export default MemberFeedbackPage;
